import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Link, MenuItem, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import { InvoiceRow, PortalApi } from './portalApi';
import { portalErrorMessage } from './portalHttp';
import { usePortalList } from './usePortalList';
import { usePortalSession } from './PortalSession';
import { saveBlob } from '../utils/saveBlob';
import { formatMoney, humanizeEnum } from '../utils/format';
import { formatDate } from '../utils/dateUtils';

// ---- P1-B customer portal ----
export const statusColor = (s: string): 'success' | 'warning' | 'default' | 'error' =>
  s === 'PAID' ? 'success' : s === 'PARTIALLY_PAID' || s === 'ISSUED' ? 'warning' : s === 'VOIDED' ? 'error' : 'default';

/** Starts a W3-T payment for the invoice and sends the browser to the gateway page. */
export const useInvoiceActions = () => {
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const pay = async (id: string) => {
    setBusyId(id); setError(null);
    try {
      const { payUrl } = await PortalApi.pay(id);
      window.location.assign(payUrl);
    } catch (e) {
      setError(portalErrorMessage(e, 'Online payment is not available for this invoice right now.'));
    } finally {
      setBusyId(null);
    }
  };
  const download = async (id: string, number: string) => {
    setBusyId(id); setError(null);
    try { saveBlob(await PortalApi.invoicePdf(id), `${number}.pdf`); }
    catch (e) { setError(portalErrorMessage(e, 'Could not download the PDF')); }
    finally { setBusyId(null); }
  };
  return { error, busyId, pay, download };
};

const STATUSES = ['ISSUED', 'PARTIALLY_PAID', 'PAID', 'VOIDED'];

const PortalInvoices: React.FC = () => {
  const { code } = usePortalSession();
  const [status, setStatus] = useState('');
  const list = usePortalList<InvoiceRow>((p, s) => PortalApi.invoices(status, p, s), [status]);
  const actions = useInvoiceActions();
  const { page, setPage, size, setSize, data, error, loading } = list;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Invoices</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField select size="small" label="Status" value={status} sx={{ flex: '1 1 180px', maxWidth: 240 }}
          onChange={e => { setStatus(e.target.value); setPage(0); }}>
          <MenuItem value="">All</MenuItem>
          {STATUSES.map(s => <MenuItem key={s} value={s}>{humanizeEnum(s)}</MenuItem>)}
        </TextField>
      </Box>
      {(error || actions.error) && <Alert severity="error" sx={{ mb: 2 }}>{error || actions.error}</Alert>}
      {loading && !data ? <CircularProgress /> : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Invoice</TableCell><TableCell>Issued</TableCell><TableCell>Due</TableCell>
                <TableCell align="right">Total</TableCell><TableCell align="right">Outstanding</TableCell>
                <TableCell>Status</TableCell><TableCell />
              </TableRow></TableHead>
              <TableBody>
                {(data?.rows ?? []).length === 0 && <TableRow><TableCell colSpan={7} align="center">No invoices.</TableCell></TableRow>}
                {(data?.rows ?? []).map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell><Link component={RouterLink} to={`/portal/${encodeURIComponent(code)}/invoices/${r.id}`}>{r.number}</Link></TableCell>
                    <TableCell>{formatDate(r.issueDate)}</TableCell>
                    <TableCell>{r.dueDate ? formatDate(r.dueDate) : '-'}</TableCell>
                    <TableCell align="right">{formatMoney(r.total, r.currency)}</TableCell>
                    <TableCell align="right">{formatMoney(r.outstanding, r.currency)}</TableCell>
                    <TableCell><Chip size="small" label={humanizeEnum(r.status)} color={statusColor(r.status)} /></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Button size="small" disabled={actions.busyId === r.id} onClick={() => actions.download(r.id, r.number)}>PDF</Button>
                      {r.payable && (
                        <Button size="small" variant="contained" sx={{ ml: 1 }} disabled={actions.busyId === r.id}
                          onClick={() => actions.pay(r.id)}>Pay now</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={data?.total ?? 0} page={page} rowsPerPage={size}
            rowsPerPageOptions={[10, 20, 50]} onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={e => { setSize(Number(e.target.value)); setPage(0); }} />
        </Paper>
      )}
    </Box>
  );
};

export default PortalInvoices;
// ---- end P1-B ----
